import { cx } from '@emotion/css';
import { Typography } from '@mui/material';
import { MouseEventHandler, ReactNode } from 'react';
import { makeStyles } from 'tss-react/mui';

import { Button } from '@/components/form/baseInputs/Button';
import { COLORS } from '@/styles/tokens/colors';
import { WithClasses } from '@/styles/types';

const useStyles = makeStyles()((_theme) => ({
  root: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 4,
    padding: `4px 8px`,
    borderStyle: 'solid',
    borderWidth: 1,
    borderRadius: 4,
  },
  interactive: {
    // React flow default is none
    // https://reactflow.dev/examples/edges/edge-label-renderer
    pointerEvents: 'all',
  },
  highlight: {},
  label: {
    color: COLORS.GRAY[900],
  },
  value: {
    color: COLORS.GRAY[900],
  },
}));

const usePrimaryStyles = makeStyles()((_theme) => ({
  root: {
    backgroundColor: COLORS.GRAY[50],
    borderColor: COLORS.GRAY[300],
    '&:hover': {
      backgroundColor: COLORS.GRAY[100],
      borderColor: COLORS.GRAY[700],
    },
  },
  interactive: {},
  highlight: {
    borderColor: COLORS.GRAY[700],
  },
  label: {},
  value: {},
}));

const useSecondaryStyles = makeStyles()((_theme) => ({
  root: {
    backgroundColor: COLORS.FUNCTIONAL.WARNING[50],
    borderColor: COLORS.FUNCTIONAL.WARNING[300],
    '&:hover': {
      backgroundColor: COLORS.FUNCTIONAL.WARNING[100],
      borderColor: COLORS.FUNCTIONAL.WARNING[700],
    },
  },
  interactive: {},
  highlight: {
    borderColor: COLORS.FUNCTIONAL.WARNING[700],
  },
  label: {},
  value: {},
}));

const useTertiaryStyles = makeStyles()((_theme) => ({
  root: {
    backgroundColor: COLORS.FUNCTIONAL.SUCCESS[50],
    borderColor: COLORS.FUNCTIONAL.SUCCESS[300],
    '&:hover': {
      backgroundColor: COLORS.FUNCTIONAL.SUCCESS[100],
      borderColor: COLORS.FUNCTIONAL.SUCCESS[400],
    },
  },
  interactive: {},
  highlight: {
    borderColor: COLORS.FUNCTIONAL.SUCCESS[700],
  },
  label: {},
  value: {},
}));

const useDestructiveStyles = makeStyles()((_theme) => ({
  root: {
    backgroundColor: COLORS.ORANGE[50],
    borderColor: COLORS.ORANGE[300],
    '&:hover': {
      backgroundColor: COLORS.ORANGE[100],
      borderColor: COLORS.ORANGE[700],
    },
  },
  interactive: {},
  highlight: {
    borderColor: COLORS.ORANGE[700],
  },
  label: {},
  value: {},
}));

const useAccentStyles = makeStyles()((_theme) => ({
  root: {
    backgroundColor: COLORS.FUNCTIONAL.SUCCESS[50],
    borderColor: COLORS.FUNCTIONAL.SUCCESS[300],
  },
  highlight: {
    borderColor: COLORS.FUNCTIONAL.SUCCESS[800],
  },
  label: {},
  value: {},
  interactive: {},
}));

export type EdgeLabelVariant =
  | 'primary'
  | 'secondary'
  | 'tertiary'
  | 'destructive'
  | 'accent';

export interface EdgeLabelProps {
  variant?: EdgeLabelVariant;
  label?: string;
  value?: string;
  endIcon?: ReactNode;
  classes?: WithClasses<typeof useStyles>;
  highlight?: boolean;
  onClick?: MouseEventHandler<HTMLButtonElement>;
  /**
   * An in-place editor that the label shows instead of its text. With an
   * editor, the label is not a button, so the editor gets its keys.
   */
  editor?: ReactNode;
}

export function EdgeLabel({
  variant = 'primary',
  label,
  value,
  endIcon,
  classes: externalClasses,
  highlight,
  onClick,
  editor,
}: EdgeLabelProps) {
  const { classes } = useStyles(undefined, {
    props: { classes: externalClasses },
  });

  const { classes: primaryClasses } = usePrimaryStyles();
  const { classes: secondaryClasses } = useSecondaryStyles();
  const { classes: tertiaryClasses } = useTertiaryStyles();
  const { classes: destructiveClasses } = useDestructiveStyles();
  const { classes: accentClasses } = useAccentStyles();
  const variantClasses: Record<EdgeLabelVariant, typeof classes> = {
    primary: primaryClasses,
    secondary: secondaryClasses,
    tertiary: tertiaryClasses,
    destructive: destructiveClasses,
    accent: accentClasses,
  };
  const variantStyles = variantClasses[variant];

  return (
    <Button
      variant="transparent"
      size="sm"
      // MUI makes a div a focusable button, which would wrap the editor.
      {...(editor && { component: 'div', role: undefined, tabIndex: -1 })}
      onClick={onClick}
      className={cx(
        variantStyles.root,
        classes.root,
        highlight && variantStyles.highlight,
        highlight && classes.highlight,
        !!onClick && variantStyles.interactive,
        !!onClick && classes.interactive
      )}
    >
      {(label || editor) && (
        <Typography
          variant="body-xs"
          className={cx(variantStyles.label, classes.label)}
        >
          {editor ?? label}
          {value && <>&nbsp;</>}
        </Typography>
      )}
      {value && (
        <Typography
          variant="body-xs"
          className={cx(variantStyles.value, classes.value)}
        >
          {value}
        </Typography>
      )}
      {endIcon}
    </Button>
  );
}
